/**
 * ДАННЫЕ СТУДИИ РАЗРАБОТКИ ИГР
 * Этот файл содержит всю информацию о команде, играх и студии
 * Обновляйте этот файл при добавлении новых игр или изменении информации
 */

// ============= ИНФОРМАЦИЯ О СТУДИИ =============
export const studioInfo = {
  name: "Indie Game Collective",
  tagline: "Творчество без границ",
  description: "Независимая инди-команда из 4 разработчиков, создающая уникальные игры",
  mission: "Мы не берем заказы - мы создаем игры как форму творческого самовыражения. Каждый наш проект - это эксперимент, история и опыт, которым мы хотим поделиться с миром.",
  philosophy: {
    ideas: "Все идеи и концепции разрабатываются внутри команды - мы создаем то, во что верим сами",
    engine: "Разрабатываем на Unity - профессиональном игровом движке",
    art: "Все спрайты и визуальные элементы создаются нашим художником с нуля",
    variety: "Работаем как с 2D, так и с 3D играми, экспериментируя с разными жанрами и механиками"
  },
  founded: "2024",
  status: "В разработке",
  contact: {
    email: "contact@gamedev.studio",
    discord: "@indie_collective",
    telegram: "@indie_games",
  },
  timezone: "Asia/Omsk", // Часовой пояс Омск (UTC+6)
  location: "Омск, Россия"
};

// ============= КОМАНДА =============
export interface TeamMember {
  id: string;
  name: string;
  role: string;
  description: string;
  icon: string;
  color: "primary" | "secondary" | "accent" | "foreground";
  skills: string[];
}

export const team: TeamMember[] = [
  {
    id: "fallen-son",
    name: "Fallen-Son",
    role: "Programmer",
    description: "Архитектор кода и технический лидер проекта",
    icon: "Code",
    color: "primary",
    skills: ["C#", "Unity", "Godot", "Gameplay Programming", "AI Systems"]
  },
  {
    id: "kapak",
    name: "kapak",
    role: "Level Designer",
    description: "Создатель уровней и архитектор игрового процесса",
    icon: "Map",
    color: "secondary",
    skills: ["Level Design", "Game Flow", "Puzzle Design", "Playtesting", "Balance"]
  },
  {
    id: "w-wagner",
    name: "W-Wagner",
    role: "Artist",
    description: "Визуальный дизайнер проекта и автор всех спрайтов",
    icon: "Palette",
    color: "accent",
    skills: ["Pixel Art", "Character Design", "UI/UX", "Visual Style", "Animation"]
  },
  {
    id: "marina",
    name: "Marina",
    role: "Game Designer",
    description: "Автор идей, документации и архитектор меню",
    icon: "BookOpen",
    color: "foreground",
    skills: ["Game Design", "Narrative Design", "UI Flow", "Documentation", "Concept Development"]
  }
];

// ============= ИГРЫ =============
export interface Game {
  id: string;
  title: string;
  tagline: string;
  description: string;
  longDescription: string;
  status: "В разработке" | "Завершена" | "Планируется" | "Прототип";
  dimension: "2D" | "3D"; // Тип игры для фильтрации
  progress: number; // Прогресс разработки (0-100%)
  releaseDate?: string;
  genre: string[];
  features: string[];
  tech: string[];
  platforms: string[];
  links?: {
    steam?: string;
    discord?: string;
    vk?: string;
  };
  images?: {
    hero?: string;
    screenshots?: string[];
  };
}

export const games: Game[] = [
  {
    id: "game-001",
    title: "CATatonia / КОТатония",
    tagline: "Платформер о маленьком котенке, который спешит домой до наступления ночи",
    description: "2D pixel-art платформер с единым динамичным уровнем, объединяющим лес, болото и город. Преодолей препятствия и доберись до дома, пока не наступила ночь!",
    longDescription: `
      CATatonia — это атмосферный 2D pixel-art платформер о маленьком котенке, который должен вернуться домой к хозяйке до наступления ночи.
      
      Игра сочетает механики Cuphead, Geometry Dash и Little Nightmares. Игроку предстоит преодолеть единый большой уровень, 
      объединяющий три уникальные локации: дремучий лес с волками и ветками, опасное болото с крокодилами и кочками, 
      и оживленный город с машинами и собаками.
      
      Ключевая особенность — система "кататонии": при столкновении с препятствием котенок замирает (звездочки над головой),
      теряя драгоценное время. Время работает как health bar — котенок знает сколько минут нужно, чтобы успеть домой.
      За каждую неудачу он теряет минуту времени.
      
      Минималистичный стиль, pixel art графика, качёвая динамичная музыка и атмосферное освещение
      создают уникальный игровой опыт о преодолении трудностей на пути к счастью.
    `,
    status: "В разработке",
    dimension: "2D",
    progress: 65,
    genre: ["2D", "Pixel Art", "Platformer", "Adventure", "Indie"],
    features: [
      "Единый динамичный уровень через лес, болото и город",
      "Система 'кататонии' с потерей времени при столкновениях",
      "Прыжки, уклонения и скольжение по мокрым поверхностям",
      "Качёвый динамичный саундтрек",
      "Время как health bar — ограниченные минуты до ночи",
      "Минималистичный pixel-art стиль",
      "Атмосферное освещение и туман"
    ],
    tech: ["Unity", "C#", "2D Platformer", "Pixel Art", "Mobile Controls"],
    platforms: ["Android", "iOS", "PC (планируется)"],
    links: {
      steam: "https://store.steampowered.com/app/catatonia",
      discord: "https://discord.gg/catatonia",
      vk: "https://vk.com/catatonia_game",
    },
    images: {
      // hero: "/images/games/catatonia/hero.jpg",
      // screenshots: ["/images/games/catatonia/screenshot-1.jpg"]
    }
  }
  // Добавляйте новые игры здесь:
  // {
  //   id: "game-002",
  //   title: "Следующая игра",
  //   dimension: "3D",
  //   progress: 20,
  //   ...
  // }
];

// ============= ДОСТИЖЕНИЯ СТУДИИ =============
export const stats = {
  games: games.length,
  team: team.length,
  experience: "1+ год",
  status: "активно работаем 24/7"
};

// ============= ТЕХНОЛОГИИ СТУДИИ =============
export const technologies = [
  "Unity",
  "Godot", 
  "C#",
  "Pixel Art",
  "Game Design",
  "Level Design"
];

// ============= СОЦИАЛЬНЫЕ СЕТИ СТУДИИ =============
export const socials = {
  discord: {
    label: "Discord студии",
    url: "https://discord.gg/indie-collective",
    handle: "@indie_collective"
  },
  vk: {
    label: "VK студии",
    url: "https://vk.com/indie_collective",
    handle: "@indie_collective"
  },
  telegram: {
    label: "Telegram студии",
    url: "https://t.me/indie_games",
    handle: "@indie_games"
  },
  steam: {
    label: "Steam студии",
    url: "https://store.steampowered.com/developer/indie-collective",
    handle: "indie-collective"
  },
  github: {
    label: "GitHub студии",
    url: "https://github.com/indie-collective",
    handle: "@indie-collective"
  }
};
